import React, { useRef } from 'react';
import { useState, useEffect, usePrintContext, Hooks, Form, Field, Name, Title, Info, Code, Actions, Toast,  API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/PrintImports';
import { Invoice } from "models/InvoiceModel";
import { User } from 'models/UserModel';
import { Doctor } from 'models/DoctorModel';
import { Client } from 'models/ClientModel';
import { Class } from 'constants/codes';
import { Payment } from 'models/PaymentModel';
import { Insurer } from 'models/InsurerModel';
import { Term } from 'models/TermModel';

const InvoicePrint: React.FC = () => {
  const width = { product: 42, price: 12, qty: 10, discount: 12, gst: 12, Amount: 12 }
  const width2 = { date: 16, receivedfrom: 30, mop: 18, receivedby: 20, amount: 16 }
  const [formId, formName] = [Code.Form.Id.INVOICE, Code.Form.Name.INVOICE];
  const {keyId, loading, setLoading, showMenu, setShowMenu, message, setMessage, error, setError, menuPosition, setMenuPosition, contextMenuRef } = usePrintContext();
  const [invoiceData, setInvoiceData] = useState<Invoice>({});
  const [paymentData, setPaymentData] = useState<Payment[]>([]); 
  const [clientData, setClientData] = useState<Client | null>(null);
  const [doctorData, setDoctorData] = useState<Doctor | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [insurerData1, setInsurerData1] = useState<Insurer | null>(null);
  const [insurerData2, setInsurerData2] = useState<Insurer | null>(null);
  
  const [fieldDispenser, setFieldDispenser] = useState<boolean>(false);
  const [fieldClientId, setFieldClientId] = useState<boolean>(true);
  const [fieldAHC, setFieldAHC] = useState<boolean>(false);
  const [fieldDOB, setFieldDOB] = useState<boolean>(false);
  const [fieldPhone, setFieldPhone] = useState<boolean>(true);
  const [fieldAddress, setFieldAddress] = useState<boolean>(true);
  const [termData, setTermData] = useState<string>(Code.Term.INVOICE);

  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  const printRef = useRef<HTMLDivElement>(null);
  Hooks.Privilege.HasPrint(Privilege);
  const companyId = Utility.Storage.CompanyId();
  
  useEffect(() => {
    API.Data.GetByKeyId<Invoice>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
      const formData = await Form.Map.Data(data);
      setInvoiceData((prevData) => ({
        ...prevData,
        ...formData,
      }));
      setClientData(await API.Data.GetClientCity(data.client_id));
      setDoctorData(await API.Data.GetDoctor(data.doctor_id));
      setUserData(await API.Data.GetUser(data.user_id));
      data.insurer1_id && data.insurer1_id > 2 && setInsurerData1(await API.Data.GetInsurer(data.insurer1_id));
      data.insurer2_id && data.insurer2_id > 2 && setInsurerData2(await API.Data.GetInsurer(data.insurer2_id));
      data.term_id && data.term_id > 2 && setTermData(await API.Data.GetTerm(data.term_id));
      callPaymentList(data.invoice_id);
    }})
    setLoading(false);
  }, []); 

  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>, rxId: number) => {
      if(rxId===Code.Exam.Map.FIELD_AHC) setFieldAHC(e.target.checked); 
      else if(rxId===Code.Exam.Map.FIELD_DOB) setFieldDOB(e.target.checked); 
      else if(rxId===Code.Exam.Map.FIELD_PHONE) setFieldPhone(e.target.checked); 
      else if(rxId===Code.Exam.Map.FIELD_ADDRESS) setFieldAddress(e.target.checked); 
      else if(rxId===Code.Exam.Map.FIELD_CLIENT_ID) setFieldClientId(e.target.checked); 
      else if(rxId===Code.Exam.Map.FIELD_DISPENSER) setFieldDispenser(e.target.checked); 
    };

  const callPaymentList = async (invoiceId: number | undefined) => {
    if (!invoiceId) return;  
      const url = API.Build.FormApiEndpint(Code.Form.Name.PAYMENT, 0, Name.Payment.Id, Code.Option.Sort.DESC, {
      fields: Utility.Join.Fields([Name.Payment.Date, Name.Payment.Number, Name.Insurer.Name, Name.Common.ModeName, Name.User.FirstName, Name.Payment.Amount]),
      [Name.Invoice.Id]: invoiceId,
    });
    await API.Data.List<Payment>(url, setPaymentData, setError, setLoading);
  };

  return (
    <Control.Print.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      {!loading && (<>
        <Messages.Toast Messages={message} Error={error} />
        <Button.Print keyId={keyId} formName={formName} Privilege={Privilege} clientData={clientData} fileName={invoiceData.invoice_number} printRef={printRef} setMessage={setMessage} setError={setError}>
          <div className='print-checklist'>
            <div className='print-checklist-options'>
              <span className='print-checklist-title'>Field Options</span>
              <div><input type='checkbox' checked={fieldClientId} onChange={(e) => handleCheckChange(e, 11)} title='Client ID' />Client ID</div>
              <div><input type='checkbox' checked={fieldAHC} onChange={(e) => handleCheckChange(e, 7)} title='Alberta Health Card' />Health Card</div>
              <div><input type='checkbox' checked={fieldDOB} onChange={(e) => handleCheckChange(e, 8)} title='Date of Birth' />Date of Birth</div>
              <div><input type='checkbox' checked={fieldPhone} onChange={(e) => handleCheckChange(e, 9)} title='Phone' />Phone</div>
              <div><input type='checkbox' checked={fieldAddress} onChange={(e) => handleCheckChange(e, 10)} title='Address' />Address</div>
              <div><input type='checkbox' checked={fieldDispenser} onChange={(e) => handleCheckChange(e, 12)} title='Dispenser' />Dispenser</div>
            </div>
          </div>
        </Button.Print>
        <Control.Print.Data ref={printRef}>
            <Control.Flex>
              <Control.Column size={[0,60,0,0]}>
                <Control.Box>
                  <Field.CompanyHead companyId={companyId} locationId={invoiceData.location_id} />
                </Control.Box>
                <Control.Box>
                  <Field.Value value={Utility.Get.FullName(clientData, Code.Form.Name.CLIENT).toUpperCase()} valueBold={true} />
                  {fieldPhone && <Field.Value value={Utility.Format.Phone(clientData?.client_phone)} />}
                  {fieldAddress && <> 
                  <Field.Value value={clientData?.client_address || ''} />
                  <Field.Value value={`${clientData?.city_name || ''} ${clientData?.client_postal || ''}`} />
                  </>}
                </Control.Box>
              </Control.Column>
              <Control.Column size={[0,40,0,0]} align={Code.Align.RIGHT}>
                <Control.Box>
                  <Field.SubHead title={formName} />
                  <Control.Form.Barcode value={invoiceData.invoice_number!} />       
                  <Field.Value title={Title.InvoiceNo} value={invoiceData.invoice_number} />
                  <Field.Value title={Title.InvoiceDate} value={Utility.Format.DateOnly(invoiceData.invoice_date)} />
                  <Field.Value title={Title.Doctor} value={Utility.Get.FullName(doctorData,Code.Form.Name.DOCTOR)} />
                  {fieldDispenser &&  <Field.Value title={Title.Dispenser} value={Utility.Get.FullName(userData,Code.Form.Name.USER)} />} 
                  {fieldClientId && <Field.Value title={Title.Client} value={`#${clientData?.client_number}`} />}
                  {fieldAHC && <Field.Value title={Title.HealthCard} value={clientData?.client_health_card} />}
                  {fieldDOB && <Field.Value title={Title.ClientDOB} value={Utility.Format.DateOnly(clientData?.client_dob)} />}
                </Control.Box>
              </Control.Column>
            </Control.Flex>
            <Control.Print.Table>
              <Control.Print.Title>
                <Control.Grid.Items values={[
                  [Title.Desc, width.product], 
                  [Title.Price, width.price, Code.Align.RIGHT], 
                  [Title.Qty, width.qty, Code.Align.CENTER],
                  [Title.Disc, width.discount, Code.Align.RIGHT],
                  [Title.GST, width.gst, Code.Align.RIGHT],
                  [Title.Amount, width.Amount, Code.Align.RIGHT]
                ]} upper={true} />
              </Control.Print.Title>
              <Control.Print.List>
                {invoiceData.items?.map((item, index) => (
                  <Control.Print.Index key={index} index={index}>
                    <Control.Print.Item>
                      <Control.Print.Product product={item.product_name} sku={item.item_skucode} comment={item.item_comment} size={[width.product]} />
                      <Control.Print.Number value={item.item_price} size={[width.price]}  />
                      <Control.Print.Text value={item.item_quantity} size={[width.qty]} align={Code.Align.CENTER} />
                      <Control.Print.Number value={item.item_discount_amount} size={[width.discount]} />
                      <Control.Print.Number value={item.item_tax1_amount} size={[width.gst]} />
                      <Control.Print.Number value={item.item_net_amount} size={[width.Amount]}  /> 
                    </Control.Print.Item>
                  </Control.Print.Index>
                ))}
              </Control.Print.List>
            </Control.Print.Table>
            <Control.Flex className={Class.ptop3}>
              <Control.Column size={[0,70,0,0]}>
                <Control.Box>
                  <Control.Print.Term value={termData} />
                </Control.Box>
              </Control.Column>
              <Control.Column size={[0,5,0,0]}><></></Control.Column>
              <Control.Column size={[0,25,0,0]} align={Code.Align.RIGHT}>
                <Control.Box>
                  <Field.Total title={Title.Gross} value={invoiceData.invoice_gross_amount} />
                  <Field.Total title={Title.Discount} value={invoiceData.invoice_discount_amount} />
                  <Field.Total title={Title.GST} value={invoiceData.invoice_tax_amount} />
                  <Field.Total title={Title.Payable} value={invoiceData.invoice_payable_amount} borderBottom={true} />
                  <Field.Total title={Title.Client} value={invoiceData.invoice_client_amount} />
                  {insurerData1 && <Field.Total title={insurerData1?.insurer_name || "Insurer"} value={invoiceData.invoice_insurer1_amount} /> }
                  {insurerData2 && <Field.Total title={insurerData2?.insurer_name || "Insurer"} value={invoiceData.invoice_insurer2_amount} /> }
                  <Field.Total title={Title.Balance} value={Number(invoiceData.invoice_payable_amount) - Number(paymentData.reduce((sum, item) => sum + (item.payment_amount ?? 0), 0)) } borderTop={true} />
                </Control.Box>
              </Control.Column>
            </Control.Flex>
            <Control.Print.Table>
                <Control.Print.Title>
                  <Control.Form.Columns values={[
                    [Title.Date, width2.date],
                    [Title.ReceivedFrom, width2.receivedfrom],
                    [Title.MOP, width2.mop],
                    [Title.ReceivedBy, width2.receivedby],
                    [Title.Amount, width2.amount, Code.Align.RIGHT]
                  ]} />
                </Control.Print.Title>
                <Control.Print.List>
                  {paymentData.map((payment, index) => { return (
                    <Control.Print.Index key={index} index={index}>
                      <Control.Print.Item>
                        <Control.Print.Date value={payment.payment_date} size={[width2.date]} />
                        <Control.Print.Text value={payment.insurer_name} size={[width2.receivedfrom]} />
                        <Control.Print.Text value={payment.mode_name} size={[width2.mop]}/>
                        <Control.Print.Text value={payment.user_first_name} size={[width2.receivedby]} />
                        <Control.Print.Number value={payment.payment_amount} size={[width2.amount]} />
                      </Control.Print.Item>
                    </Control.Print.Index>
                  )})}
                </Control.Print.List>
              </Control.Print.Table>
              
            <Field.PrintedOn />
        </Control.Print.Data>  
      </>)}
      {showMenu && (<div ref={contextMenuRef}><Control.PrintContextMenu keyId={keyId} showMenu={showMenu} formName={formName}  setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Print />
    </Control.Print.Frame>
  );
};

export default InvoicePrint;